import { Container } from "@mui/material";

import {
  ActionIcon,
  Badge,
  Button,
  CopyButton,
  Divider,
  Loader,
  Modal,
  Pagination,
  Select,
  Table,
  Tabs,
  Text,
  TextInput,
  TextInputProps,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { GridRenderCellParams } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import DatagridDisplay from "../../Components/DatagridDisplay";
import DisplayAlert from "../../Components/DisplayAlert";
import MegaLoader from "../../Components/MegaLoader";
import {
  BrandImages,
  Colors,
  ValidIDLength,
  ValidMinimumTransactionReferenceLength,
} from "../../Lib/Data";
import { Endpoints } from "../../Lib/Endpoints";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  DefaultWarningNotification,
  generateRandomString,
  getFinancialValueFromNumeric,
  getPaginationTotal,
} from "../../Lib/Methods";
import { NonPaginatedResponse, PaginatedResponse } from "../../Lib/Responses";
import {
  OfflinePaymentProps,
  PaginationProps,
  PlanProps,
  PossibleServices,
  TransactionProps,
  UserProfileProps,
} from "../../Lib/Types";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import "./styles.scss";
import {
  IconClipboard,
  IconExternalLink,
  IconReload,
  IconSearch,
} from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { Link } from "react-router-dom";
const TransactionsPage: React.FC = () => {
  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 20,
  });
  const {
    data: transactions,
    reloadData: reloadTransactions,
    isLoading: isLoadingTransactions,
  } = usePerformRequest<PaginatedResponse<TransactionProps[]>>({
    method: "GET",
    route: Endpoints.GetTransactionsPaginated(
      pagination.page,
      pagination.limit
    ),
  });

  const [currentTransaction, setCurrentTransaction] =
    useState<TransactionProps>();
  const [currentUser, setCurrentUser] = useState<UserProfileProps>();
  const [isLoadingUser, setLoadingUser] = useState<boolean>(false);
  const [isLoadingOffline, setLoadingOffline] = useState<boolean>(false);
  const [
    isTransactionModalOpened,
    { open: OpenTransactionModal, close: CloseTransactionModal },
  ] = useDisclosure(false);

  useEffect(() => {
    const GetUserProfile = async () => {
      setLoadingUser(true);
      if (currentTransaction && currentTransaction.userID) {
        const r = await PerformRequest<NonPaginatedResponse<UserProfileProps>>({
          method: "GET",
          route: Endpoints.GetUserProfile(currentTransaction.userID),
          callback: () => {
            setLoadingUser(false);
          },
        });
        setLoadingUser(false);
        if (r) {
          if (r.status === 404) {
            DefaultErrorNotification("User does not exist!");
          }
          if (r.status === 200) {
            setCurrentUser(r.data.data);
          }
        }
      }
    };
    const GetOfflinePayment = async () => {
      setLoadingOffline(true);
      if (currentTransaction && currentTransaction.userID) {
        const r = await PerformRequest<
          NonPaginatedResponse<OfflinePaymentProps>
        >({
          method: "GET",
          route: Endpoints.GetSingleOfflinePayment(
            currentTransaction.reference
          ),
          callback: () => {
            setLoadingOffline(false);
          },
        });
        setLoadingOffline(false);
        if (r) {
          if (r.status === 404) {
            DefaultErrorNotification("Offline does not exist!");
          }
          if (r.status === 200) {
            setCurrentOffline(r?.data?.data);
            setServiceToAssign(r?.data?.data?.services[0]);
          }
        }
      }
    };
    if (currentTransaction && currentTransaction.userID) {
      GetUserProfile();
    }
    if (currentTransaction && currentTransaction.provider === "offline") {
      GetOfflinePayment();
    }
  }, [currentTransaction]);

  const [foundTransactions, setFoundTransactions] = useState<
    TransactionProps[]
  >([]);

  const [isSearching, setSearching] = useState<boolean>(false);

  const transactionIDForm = useForm({
    initialValues: {
      transactionID: "",
    },
    validate: {
      transactionID: (value) =>
        value.length !== ValidIDLength ? "Invalid Transaction ID" : null,
    },
  });
  const userIDForm = useForm({
    initialValues: {
      userID: "",
    },
    validate: {
      userID: (value) =>
        value.length !== ValidIDLength ? "Invalid User ID" : null,
    },
  });
  const referenceForm = useForm({
    initialValues: {
      reference: "",
    },
    validate: {
      reference: (value) =>
        value.length < ValidMinimumTransactionReferenceLength
          ? "Invalid Reference"
          : null,
    },
  });
  const FilterByTransactionID = async () => {
    const { hasErrors } = transactionIDForm.validate();
    if (!hasErrors) {
      setSearching(true);
      const { transactionID } = transactionIDForm.values;
      const r = await PerformRequest<NonPaginatedResponse<TransactionProps>>({
        route: Endpoints.GetSingleTransaction(transactionID),
        callback: () => {
          setSearching(false);
        },
        method: "GET",
      });
      setSearching(false);
      if (r && r.status === 200) {
        setFoundTransactions(r?.data?.data ? [r?.data.data] : []);
      } else {
        DefaultErrorNotification("Could not get transaction!");
      }
    }
  };
  const FilterByUserID = async () => {
    const { hasErrors } = userIDForm.validate();
    if (!hasErrors) {
      setSearching(true);
      const { userID } = userIDForm.values;
      const r = await PerformRequest<NonPaginatedResponse<TransactionProps[]>>({
        route: Endpoints.GetTransactionsByUserID(userID),
        callback: () => {
          setSearching(false);
        },
        method: "GET",
      });
      setSearching(false);
      if (r && r.status === 200) {
        setFoundTransactions(r?.data?.data ?? []);
      } else {
        DefaultErrorNotification("Could not get transactions!");
      }
    }
  };
  const FilterByTransactionReference = async () => {
    const { hasErrors } = referenceForm.validate();
    if (!hasErrors) {
      setSearching(true);
      const { reference } = referenceForm.values;
      const r = await PerformRequest<NonPaginatedResponse<TransactionProps>>({
        route: Endpoints.GetSingleTransactionByReference(reference),
        callback: () => {
          setSearching(false);
        },
        method: "GET",
      });
      setSearching(false);
      if (r && r.status === 200) {
        setFoundTransactions(r?.data?.data ? [r?.data?.data] : []);
      } else if (r.status === 404) {
        DefaultErrorNotification("Transaction does not exist!");
      } else {
        DefaultErrorNotification("Could not get transaction!");
      }
    }
  };

  const [currentOffline, setCurrentOffline] = useState<OfflinePaymentProps>();

  const [suggestedPlans, setSuggestedPlans] = useState<PlanProps[]>([]);
  const [isLoadingPlans, setLoadingPlans] = useState<boolean>(false);
  const [isAssigning, setAssigning] = useState<boolean>(false);

  const [serviceToAssign, setServiceToAssign] = useState<PossibleServices>();
  const AssignUserToPlan = async () => {
    setAssigning(true);
    const r = await PerformRequest({
      method: "POST",
      route: Endpoints.GetSubscribeOfflinePayment(
        currentTransaction?.id as string
      ),
      data: {
        userID: currentOffline?.userID,
        planID: suggestedPlans[0].id,
        service: serviceToAssign,
        duration: currentOffline?.duration,
        tempID: currentOffline?.id,
      },
      callback: () => {
        setAssigning(false);
      },
    });
    setAssigning(false);
    if (r) {
      if (r.status === 200) {
        DefaultSuccessNotification("User assigned to plan!");

        setSuggestedPlans([]);
      } else {
        DefaultErrorNotification("An error occurred");
      }
    } else {
      DefaultErrorNotification("Could not assign user to plan!");
    }
  };
  const FindSuggestedPlans = async () => {
    setLoadingPlans(true);
    const r = await PerformRequest<PaginatedResponse<PlanProps[]>>({
      method: "POST",
      route: Endpoints.GetPlans(1, 1),
      data: {
        service: serviceToAssign,
        status: "active",
        isFilledSlots: false,
      },
      callback: () => {
        setLoadingPlans(false);
      },
    });
    setLoadingPlans(false);
    if (r) {
      if (r.status === 200) {
        if (r.data.data.length > 0) {
          setSuggestedPlans(r.data.data);
        } else {
          DefaultWarningNotification("There are no available plans!");
        }
      } else {
        DefaultErrorNotification("Could not get plans!");
      }
    }
  };
  useEffect(() => {
    const GetUserProfile = async () => {
      if (currentOffline && currentOffline.userID) {
        const r = await PerformRequest<NonPaginatedResponse<UserProfileProps>>({
          method: "GET",
          route: Endpoints.GetUserProfile(currentOffline.userID),
        });
        if (r) {
          if (r.status === 404) {
            DefaultErrorNotification("User does not exist!");
          }
          if (r.status === 200) {
            setCurrentUser(r.data.data);
          }
        }
      }
    };

    if (currentOffline && currentOffline.userID) {
      GetUserProfile();
      FindSuggestedPlans();
    }
  }, [currentOffline]);

  useEffect(() => {
    if (serviceToAssign && currentOffline && serviceToAssign.length > 0) {
      FindSuggestedPlans();
    }
  }, [serviceToAssign]);

  const filterInputProps: TextInputProps = {
    w: 250,
    classNames: {
      input: "filter-input",
    },
  };
  return (
    <Container maxWidth="lg">
      <div className="transactions-container">
        <div className="component-header flex-row align-center justify-between">
          <Text className="label">Transactions</Text>
          <Button
            onClick={reloadTransactions}
            loading={isLoadingTransactions}
            leftSection={<IconReload size={16} />}
          >
            Reload
          </Button>
        </div>
        <br />
        <Tabs defaultValue="all">
          <Tabs.List grow>
            <Tabs.Tab value="all" leftSection={<IconClipboard size={17} />}>
              All
            </Tabs.Tab>
            <Tabs.Tab value="search" leftSection={<IconSearch size={17} />}>
              Search
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="all">
            {transactions && transactions.data && (
              <>
                <DatagridDisplay
                  sx={{
                    marginTop: 3,
                  }}
                  rows={transactions?.data}
                  onRowClick={(params) => {
                    OpenTransactionModal();
                    setCurrentTransaction(params.row);
                  }}
                  isLoading={isLoadingTransactions}
                  columns={[
                    {
                      field: "createdAt",
                      width: 210,
                      headerName: "Date",
                      renderCell: (
                        params: GridRenderCellParams<TransactionProps>
                      ) => {
                        return (
                          <span>
                            {dayjs(params.row.createdAt).format("DD MMM YYYY")}
                            &nbsp;at&nbsp;
                            {dayjs(params.row.createdAt).format("hh:mmA")}
                          </span>
                        );
                      },
                    },
                    {
                      field: "reference",
                      width: 150,

                      headerName: "Reference",
                      renderCell: (
                        params: GridRenderCellParams<TransactionProps>
                      ) => {
                        return (
                          <CopyButton value={params.row.reference}>
                            {({ copied, copy }) => (
                              <Tooltip
                                label={copied ? "Copied!" : "Copy to clipboard"}
                              >
                                <span
                                  className="pointer"
                                  onClick={copy}
                                  style={{
                                    color: copied
                                      ? Colors.GreenPrimary
                                      : Colors.BluePrimary,
                                  }}
                                >
                                  {params.row.reference}
                                </span>
                              </Tooltip>
                            )}
                          </CopyButton>
                        );
                      },
                    },
                    {
                      field: "amount",
                      width: 140,

                      headerName: "Amount",
                      renderCell: (
                        params: GridRenderCellParams<TransactionProps>
                      ) => {
                        return (
                          <span className="amount">
                            ₦{getFinancialValueFromNumeric(params.row.amount)}
                          </span>
                        );
                      },
                    },
                    {
                      field: "isSubscription",
                      width: 140,

                      headerName: "Subscription",
                      renderCell: (
                        params: GridRenderCellParams<TransactionProps>
                      ) => {
                        return (
                          <span>
                            {params.row.provider === "offline" ? (
                              <>
                                {params.row.isSubscription ? (
                                  <Badge color={Colors.GreenPrimary}>YES</Badge>
                                ) : params.row.type === "new" ? (
                                  <>
                                    <Badge color={Colors.RedPrimary}>
                                      SHOULD
                                    </Badge>
                                  </>
                                ) : (
                                  <Badge color={Colors.BluePrimary}>NO</Badge>
                                )}
                              </>
                            ) : (
                              <>Unset</>
                            )}
                          </span>
                        );
                      },
                    },
                    {
                      field: "duration",

                      headerName: "Duration",
                      renderCell: (
                        params: GridRenderCellParams<TransactionProps>
                      ) => {
                        return <span>{params.row.duration}</span>;
                      },
                    },
                    {
                      field: "type",
                      width: 140,
                      headerName: "Type",
                      renderCell: (
                        params: GridRenderCellParams<TransactionProps>
                      ) => {
                        return <span>{params.row.type}</span>;
                      },
                    },
                    {
                      field: "services",
                      width: 210,

                      headerName: "Services",
                      renderCell: (
                        params: GridRenderCellParams<TransactionProps>
                      ) => {
                        return (
                          <div className="flex-row align-center service-images">
                            {params.row.services.map((service) => {
                              return (
                                <img
                                  src={BrandImages.retrieveImage(service)}
                                  alt=""
                                  className="image"
                                />
                              );
                            })}
                          </div>
                        );
                      },
                    },
                  ]}
                />
                {currentTransaction && (
                  <Modal
                    opened={isTransactionModalOpened}
                    onClose={() => {
                      CloseTransactionModal();
                      setCurrentTransaction(undefined);
                      setCurrentUser(undefined);
                    }}
                    title="Transaction Details"
                    centered
                    size="lg"
                  >
                    <Table variant="vertical" layout="fixed" withTableBorder>
                      <Table.Thead>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th w={200}>Transaction ID</Table.Th>
                          <Table.Td>
                            <CopyButton value={currentTransaction.id}>
                              {({ copied, copy }) => (
                                <Tooltip
                                  label={
                                    copied ? "Copied!" : "Copy to clipboard"
                                  }
                                >
                                  <div
                                    className="pointer flex-row align-center"
                                    onClick={copy}
                                    style={{
                                      color: copied
                                        ? Colors.GreenPrimary
                                        : Colors.BluePrimary,
                                      height: "100%",
                                    }}
                                  >
                                    {currentTransaction.id}
                                  </div>
                                </Tooltip>
                              )}
                            </CopyButton>
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th w={200}>Reference</Table.Th>
                          <Table.Td>
                            <CopyButton value={currentTransaction.reference}>
                              {({ copied, copy }) => (
                                <Tooltip
                                  label={
                                    copied ? "Copied!" : "Copy to clipboard"
                                  }
                                >
                                  <div
                                    className="pointer flex-row align-center"
                                    onClick={copy}
                                    style={{
                                      color: copied
                                        ? Colors.GreenPrimary
                                        : Colors.BluePrimary,
                                      height: "100%",
                                    }}
                                  >
                                    {currentTransaction.reference}
                                  </div>
                                </Tooltip>
                              )}
                            </CopyButton>
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Date</Table.Th>
                          <Table.Td>
                            {dayjs(currentTransaction.createdAt).format(
                              "DD MMM YYYY - hh:mmA"
                            )}
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Service</Table.Th>
                          <Table.Td>
                            <div className="flex-row align-center temp-service-images">
                              {currentTransaction.services.map((service) => {
                                return (
                                  <img
                                    src={BrandImages.retrieveImage(service)}
                                    alt=""
                                    className="image"
                                  />
                                );
                              })}
                            </div>
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Amount</Table.Th>
                          <Table.Td fw={600} c={Colors.GreenSecondary}>
                            ₦{currentTransaction.amount}
                          </Table.Td>
                        </Table.Tr>
                        {currentTransaction.coupon && (
                          <Table.Tr key={generateRandomString(12)}>
                            <Table.Th>Coupon Code</Table.Th>
                            <Table.Td>
                              {currentTransaction.coupon.code}
                            </Table.Td>
                          </Table.Tr>
                        )}
                        {currentTransaction.coupon && (
                          <Table.Tr key={generateRandomString(12)}>
                            <Table.Th>Coupon Discount</Table.Th>
                            <Table.Td fw={600} c={Colors.GreenSecondary}>
                              {currentTransaction.coupon.discount}%
                            </Table.Td>
                          </Table.Tr>
                        )}
                      </Table.Thead>
                    </Table>

                    <Divider w="100%" mt={15} mb={10} />
                    <Text fw={500} mb={10} c={Colors.GreenSecondary}>
                      User Details
                    </Text>
                    {currentUser && currentUser && currentUser.id ? (
                      <>
                        <Table
                          variant="vertical"
                          layout="fixed"
                          withTableBorder
                        >
                          <Table.Thead>
                            <Table.Tr key={generateRandomString(12)}>
                              <Table.Th w={200}>User ID</Table.Th>
                              <Table.Td>
                                <div className="flex-row align-center">
                                  <CopyButton value={currentUser.id}>
                                    {({ copied, copy }) => (
                                      <Tooltip
                                        label={
                                          copied
                                            ? "Copied!"
                                            : "Copy to clipboard"
                                        }
                                      >
                                        <div
                                          className="pointer flex-row align-center"
                                          onClick={copy}
                                          style={{
                                            color: copied
                                              ? Colors.GreenPrimary
                                              : Colors.BluePrimary,
                                            height: "100%",
                                          }}
                                        >
                                          {currentUser.id}
                                        </div>
                                      </Tooltip>
                                    )}
                                  </CopyButton>
                                  &nbsp; &nbsp;
                                  <Tooltip label="View User Profile">
                                    <Link
                                      to={`/dashboard/user/${currentUser.id}`}
                                    >
                                      <ActionIcon
                                        color={Colors.BluePrimary}
                                        variant="outline"
                                      >
                                        <IconExternalLink size={16} />
                                      </ActionIcon>
                                    </Link>
                                  </Tooltip>
                                </div>
                              </Table.Td>
                            </Table.Tr>
                            <Table.Tr key={generateRandomString(12)}>
                              <Table.Th w={200}>Full Name</Table.Th>
                              <Table.Td>{currentUser.name}</Table.Td>
                            </Table.Tr>
                            <Table.Tr key={generateRandomString(12)}>
                              <Table.Th>Date Joined</Table.Th>
                              <Table.Td>
                                {dayjs(currentUser.createdAt).format(
                                  "DD MMM YYYY - hh:mmA"
                                )}
                              </Table.Td>
                            </Table.Tr>
                            <Table.Tr key={generateRandomString(12)}>
                              <Table.Th>Email</Table.Th>
                              <Table.Td>
                                <CopyButton value={currentUser.email}>
                                  {({ copied, copy }) => (
                                    <Tooltip
                                      label={
                                        copied
                                          ? "Email Copied!"
                                          : "Copy to clipboard"
                                      }
                                    >
                                      <div
                                        className="pointer flex-row align-center"
                                        onClick={copy}
                                        style={{
                                          color: copied
                                            ? Colors.GreenPrimary
                                            : Colors.BluePrimary,
                                          height: "100%",
                                        }}
                                      >
                                        {currentUser.email}
                                      </div>
                                    </Tooltip>
                                  )}
                                </CopyButton>
                              </Table.Td>
                            </Table.Tr>
                            <Table.Tr key={generateRandomString(12)}>
                              <Table.Th>Promoter</Table.Th>
                              <Table.Td>
                                <Badge
                                  color={
                                    currentUser.isPromoter
                                      ? Colors.GreenPrimary
                                      : Colors.BluePrimary
                                  }
                                >
                                  {currentUser.isPromoter ? "YES" : "NO"}
                                </Badge>
                              </Table.Td>
                            </Table.Tr>
                          </Table.Thead>
                        </Table>
                      </>
                    ) : (
                      <>
                        {isLoadingUser ? (
                          <div className="flex-row align-center justify-center width-100">
                            <Loader my={20} size={34} />
                          </div>
                        ) : (
                          <DisplayAlert
                            title="No User Found"
                            children={
                              <Text>
                                For some reason, this user does not exist :-(
                              </Text>
                            }
                          />
                        )}
                      </>
                    )}
                    <Divider w="100%" mt={15} mb={10} />
                    {suggestedPlans &&
                    suggestedPlans[0] &&
                    suggestedPlans[0].id ? (
                      <>
                        <Table
                          variant="vertical"
                          layout="fixed"
                          withTableBorder
                        >
                          <Table.Thead>
                            <Table.Tr key={generateRandomString(12)}>
                              <Table.Th w={200}>Plan ID</Table.Th>
                              <Table.Td>{suggestedPlans[0].id}</Table.Td>
                            </Table.Tr>
                            <Table.Tr key={generateRandomString(12)}>
                              <Table.Th w={200}>Login Email</Table.Th>
                              <Table.Td>
                                {suggestedPlans[0]?.login?.email}
                              </Table.Td>
                            </Table.Tr>
                            <Table.Tr key={generateRandomString(12)}>
                              <Table.Th w={200}>Available Slots</Table.Th>
                              <Table.Td>
                                {suggestedPlans[0].slots -
                                  suggestedPlans[0]?.users?.length}
                              </Table.Td>
                            </Table.Tr>
                            <Table.Tr key={generateRandomString(12)}>
                              <Table.Th>Expiration Date</Table.Th>
                              <Table.Td>
                                {dayjs(
                                  suggestedPlans[0]?.expirationDate
                                ).format("DD MMM YYYY - hh:mmA")}
                              </Table.Td>
                            </Table.Tr>
                            <Table.Tr key={generateRandomString(12)}>
                              <Table.Th>Service</Table.Th>
                              <Table.Td>
                                <div className="flex-row align-center temp-service-images">
                                  <img
                                    src={BrandImages.retrieveImage(
                                      suggestedPlans[0].service
                                    )}
                                    alt=""
                                    className="image"
                                  />
                                </div>
                              </Table.Td>
                            </Table.Tr>
                          </Table.Thead>
                        </Table>
                        <Select
                          allowDeselect={false}
                          data={currentOffline?.services.map((s) => {
                            return {
                              value: s,
                              label: s,
                            };
                          })}
                          onChange={(value) =>
                            setServiceToAssign(value as PossibleServices)
                          }
                          label="Select Service"
                          w={150}
                          mt={20}
                        />
                        <Button
                          onClick={() => AssignUserToPlan()}
                          loading={isAssigning}
                          w={150}
                          disabled={
                            currentTransaction.type === "renewal" ||
                            !serviceToAssign ||
                            serviceToAssign.length < 1
                          }
                          mt={20}
                          color={Colors.GreenPrimary}
                        >
                          Assign
                        </Button>
                        <Text fw={400} c={Colors.GraySecondary} fz={14}>
                          This will automatically add the user to the plan
                        </Text>
                      </>
                    ) : (
                      <>
                        {isLoadingPlans ? (
                          <div className="flex-row align-center justify-center width-100">
                            <Loader my={20} size={34} />
                          </div>
                        ) : (
                          <DisplayAlert
                            title="No Plans Found"
                            children={
                              <Text>
                                There are no active plans with available slots!
                              </Text>
                            }
                          />
                        )}
                      </>
                    )}
                  </Modal>
                )}
                <div className="flex-row align-center justify-center width-100">
                  <Pagination
                    value={pagination.page}
                    onChange={(value) => {
                      setPagination({ ...pagination, page: value });
                    }}
                    my={40}
                    disabled={
                      transactions?.data.length === 0 || isLoadingTransactions
                    }
                    total={getPaginationTotal(
                      transactions?.totalRecords,
                      pagination.limit
                    )}
                  />
                </div>
              </>
            )}
            {isLoadingTransactions && (
              <MegaLoader
                containerStyle={{
                  marginTop: "30px",
                }}
              />
            )}
          </Tabs.Panel>
          <Tabs.Panel value="search" className="search-tab">
            <Text mt={20} fw={500} c={Colors.GreenSecondary}>
              Search For Tranasctions
            </Text>
            <div className="flex-row justify-between search-row width-100">
              <div className="flex-col col">
                <TextInput
                  {...transactionIDForm.getInputProps("transactionID")}
                  {...filterInputProps}
                  spellCheck={false}
                  placeholder={`Enter ${ValidIDLength} character Transaction ID`}
                  label="Transaction ID"
                />
                <Button
                  w={100}
                  onClick={FilterByTransactionID}
                  loading={isSearching}
                  color={Colors.GreenPrimary}
                >
                  Filter
                </Button>
              </div>
              <div className="flex-col col">
                <TextInput
                  {...userIDForm.getInputProps("userID")}
                  {...filterInputProps}
                  spellCheck={false}
                  placeholder={`Enter ${ValidIDLength} character User ID`}
                  label="User ID"
                />
                <Button
                  w={100}
                  onClick={FilterByUserID}
                  loading={isSearching}
                  color={Colors.GreenPrimary}
                >
                  Filter
                </Button>
              </div>
              <div className="flex-col col">
                <TextInput
                  {...referenceForm.getInputProps("reference")}
                  {...filterInputProps}
                  spellCheck={false}
                  placeholder={`Minimum: ${ValidMinimumTransactionReferenceLength}`}
                  label="Transaction Reference"
                />
                <Button
                  w={100}
                  onClick={FilterByTransactionReference}
                  loading={isSearching}
                  color={Colors.GreenPrimary}
                >
                  Filter
                </Button>
              </div>
            </div>
            <Divider w="100%" my={20} />
            {isSearching ? (
              <MegaLoader />
            ) : (
              <>
                {foundTransactions.length > 0 ? (
                  <>
                    <DatagridDisplay
                      sx={{
                        marginTop: 3,
                      }}
                      rows={foundTransactions}
                      onRowClick={(params) => {
                        OpenTransactionModal();
                        setCurrentTransaction(params.row);
                      }}
                      isLoading={isLoadingTransactions}
                      columns={[
                        {
                          field: "createdAt",
                          width: 210,
                          headerName: "Date",
                          renderCell: (
                            params: GridRenderCellParams<TransactionProps>
                          ) => {
                            return (
                              <span>
                                {dayjs(params.row.createdAt).format(
                                  "DD MMM YYYY"
                                )}
                                &nbsp;at&nbsp;
                                {dayjs(params.row.createdAt).format("hh:mmA")}
                              </span>
                            );
                          },
                        },
                        {
                          field: "reference",
                          width: 150,

                          headerName: "Reference",
                          renderCell: (
                            params: GridRenderCellParams<TransactionProps>
                          ) => {
                            return (
                              <CopyButton value={params.row.reference}>
                                {({ copied, copy }) => (
                                  <Tooltip
                                    label={
                                      copied ? "Copied!" : "Copy to clipboard"
                                    }
                                  >
                                    <span
                                      className="pointer"
                                      onClick={copy}
                                      style={{
                                        color: copied
                                          ? Colors.GreenPrimary
                                          : Colors.BluePrimary,
                                      }}
                                    >
                                      {params.row.reference}
                                    </span>
                                  </Tooltip>
                                )}
                              </CopyButton>
                            );
                          },
                        },
                        {
                          field: "amount",
                          width: 140,

                          headerName: "Amount",
                          renderCell: (
                            params: GridRenderCellParams<TransactionProps>
                          ) => {
                            return (
                              <span className="amount">
                                ₦
                                {getFinancialValueFromNumeric(
                                  params.row.amount
                                )}
                              </span>
                            );
                          },
                        },
                        {
                          field: "duration",

                          headerName: "Duration",
                          renderCell: (
                            params: GridRenderCellParams<TransactionProps>
                          ) => {
                            return <span>{params.row.duration}</span>;
                          },
                        },
                        {
                          field: "services",
                          width: 210,

                          headerName: "Services",
                          renderCell: (
                            params: GridRenderCellParams<TransactionProps>
                          ) => {
                            return (
                              <div className="flex-row align-center service-images">
                                {params.row.services.map((service) => {
                                  return (
                                    <img
                                      src={BrandImages.retrieveImage(service)}
                                      alt=""
                                      className="image"
                                    />
                                  );
                                })}
                              </div>
                            );
                          },
                        },
                        {
                          field: "type",
                          width: 140,
                          headerName: "Type",
                          renderCell: (
                            params: GridRenderCellParams<TransactionProps>
                          ) => {
                            return <span>{params.row.type}</span>;
                          },
                        },
                      ]}
                    />
                  </>
                ) : (
                  <DisplayAlert
                    sx={{
                      marginTop: "30px",
                    }}
                    title="No Transactions Found"
                    children={
                      <Text>
                        No transactions were found for this search. Please try
                        again.
                      </Text>
                    }
                  />
                )}
              </>
            )}
          </Tabs.Panel>
        </Tabs>
      </div>
    </Container>
  );
};
export default TransactionsPage;
